import { useMemo, useState, type FormEvent } from 'react';

import { IconRefreshCw, IconSearch } from '../../components/layout/icons';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { SortableTh } from '../../components/ui/SortableTh';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel, fetchAllPages } from '../../lib/excel-export';
import { useTableSort } from '../../lib/table-sort';
import { formatIpdDateTime } from '../ipd-daycare/ipd-daycare.utils';
import { ReportAnalyticsGrid, ReportBarChartCard, ReportDonutChartCard, type ReportChartDatum } from './ReportAnalytics';
import { reportChartColor } from './report-chart-colors';
import { lastMonthDateFilters } from './report-filters';
import { fetchBirthReport } from './reports.api';
import { useBirthReport } from './reports.queries';
import type { BirthReportFilters, BirthReportRow } from './types';

const initialFilters = (): BirthReportFilters => ({ page: 1, limit: 25, ...lastMonthDateFilters(), value: '', gender: '', deliveryMode: '' });
const text = (value: string | null | undefined) => value || '-';
const upper = (value: string | null | undefined) => value?.trim() ? value.trim().toUpperCase() : '-';
const doctorText = (value: string) => {
  const name = value.trim().replace(/^(dr\.?\s+)+/i, '');
  return name && name !== '-' ? `DR. ${name}` : '-';
};

export const BirthReportPage = () => {
  const [draft, setDraft] = useState<BirthReportFilters>(initialFilters);
  const [filters, setFilters] = useState<BirthReportFilters>(draft);
  const query = useBirthReport(filters);
  const tableSort = useTableSort(filters.sortBy ?? null, filters.sortOrder === 'desc' ? 'desc' : 'asc', (next) => {
    setFilters((current) => ({ ...current, sortBy: next.key, sortOrder: next.direction, page: 1 }));
  });

  const applyFilters = (event: FormEvent) => {
    event.preventDefault();
    setFilters((current) => ({ ...draft, sortBy: current.sortBy, sortOrder: current.sortOrder, page: 1 }));
  };
  const reset = () => {
    const next = initialFilters();
    setDraft(next);
    setFilters(next);
  };
  const changeLimit = (limit: number) => {
    setDraft((current) => ({ ...current, limit }));
    setFilters((current) => ({ ...current, limit, page: 1 }));
  };
  const rows = query.data?.data ?? [];
  const summary = query.data?.summary;
  const genderChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'Male', value: summary?.gender.male ?? 0, color: '#3777b7' },
    { name: 'Female', value: summary?.gender.female ?? 0, color: '#b1424d' },
    { name: 'Others', value: summary?.gender.others ?? 0, color: '#c98a1c' },
  ], [summary?.gender.female, summary?.gender.male, summary?.gender.others]);
  const deliveryChartData = useMemo<ReportChartDatum[]>(() => [
    { name: 'NORMAL', Count: summary?.deliveryMode.normal ?? 0 },
    { name: 'LUCS', Count: summary?.deliveryMode.lucs ?? 0 },
  ], [summary?.deliveryMode.lucs, summary?.deliveryMode.normal]);
  const doctorChartData = useMemo<ReportChartDatum[]>(() => [...(summary?.byDoctor ?? [])]
    .sort((left, right) => right.count - left.count)
    .slice(0, 8)
    .map((item, index) => ({
      name: item.doctor,
      Births: item.count,
      color: reportChartColor(index),
    })), [summary?.byDoctor]);
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Birth report could not be loaded.';

  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<BirthReportRow>((page, limit) => fetchBirthReport({ ...filters, page, limit }), 500);
    await exportRowsToExcel({
      fileName: `Birth-Report-${new Date().toISOString().slice(0, 10)}`,
      title: 'Birth Report',
      subtitle: filters.fromDate && filters.toDate ? `${filters.fromDate} to ${filters.toDate}` : 'All dates',
      columns: [
        { header: 'Name', width: 22, map: (row) => upper(row.name) },
        { header: 'Gender', width: 12, map: (row) => upper(row.gender) },
        { header: 'Guardian', width: 22, map: (row) => upper(row.guardianName) },
        { header: 'Doctor', width: 24, map: (row) => upper(doctorText(row.doctorName)) },
        { header: 'Birth Date', width: 20, map: (row) => row.dateOfBirth ? formatIpdDateTime(row.dateOfBirth) : '-' },
        { header: 'Weight', width: 12, map: (row) => upper(row.weight) },
        { header: 'Delivery Mode', width: 16, map: (row) => upper(row.deliveryMode) },
        { header: 'Baby Status', width: 14, map: (row) => upper(row.babyStatus) },
      ],
      rows: exportRows,
    });
  };

  return (
    <div className="page-stack report-list-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">Reports</p>
          <h1>Birth Report</h1>
          <p>Birth records by gender, delivery mode, doctor, guardian, and birth date.</p>
        </div>
      </div>

      <form className="report-list-filter-card birth-report-filters" onSubmit={applyFilters}>
        <label>
          From date
          <input type="date" value={draft.fromDate ?? ''} max={draft.toDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, fromDate: event.target.value }))} />
        </label>
        <label>
          To date
          <input type="date" value={draft.toDate ?? ''} min={draft.fromDate || undefined} onChange={(event) => setDraft((current) => ({ ...current, toDate: event.target.value }))} />
        </label>
        <label>
          Gender
          <select value={draft.gender ?? ''} onChange={(event) => setDraft((current) => ({ ...current, gender: event.target.value as BirthReportFilters['gender'] }))}>
            <option value="">All Gender</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
            <option value="Others">Others</option>
          </select>
        </label>
        <label>
          Delivery
          <select value={draft.deliveryMode ?? ''} onChange={(event) => setDraft((current) => ({ ...current, deliveryMode: event.target.value as BirthReportFilters['deliveryMode'] }))}>
            <option value="">All Delivery</option>
            <option value="NORMAL">NORMAL</option>
            <option value="LUCS">LUCS</option>
          </select>
        </label>
        <label>
          Search
          <input value={draft.value ?? ''} onChange={(event) => setDraft((current) => ({ ...current, value: event.target.value }))} placeholder="Baby, guardian, doctor" />
        </label>
        <button className="button button-primary report-list-action" type="submit"><IconSearch />Search</button>
        <button className="button button-secondary report-list-action" type="button" onClick={reset}><IconRefreshCw />Reset</button>
        <ExcelExportButton className="report-list-action" onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
      </form>

      <section className="report-summary-grid">
        <SummaryTile label="Births" value={summary?.count ?? 0} />
        <SummaryTile label="Male" value={summary?.gender.male ?? 0} />
        <SummaryTile label="Female" value={summary?.gender.female ?? 0} />
        <SummaryTile label="NORMAL" value={summary?.deliveryMode.normal ?? 0} />
        <SummaryTile label="LUCS" value={summary?.deliveryMode.lucs ?? 0} />
      </section>

      {query.isSuccess ? (
        <ReportAnalyticsGrid columns={3}>
          <ReportDonutChartCard eyebrow="Gender analysis" title="Birth gender distribution" data={genderChartData} />
          <ReportBarChartCard
            eyebrow="Delivery analysis"
            title="Delivery mode comparison"
            data={deliveryChartData}
            bars={[{ key: 'Count', name: 'Births', color: '#0d6b66' }]}
          />
          <ReportBarChartCard
            eyebrow="Doctor analysis"
            title="Births by doctor"
            data={doctorChartData}
            bars={[{ key: 'Births', name: 'Births', color: '#3777b7' }]}
            layout="vertical"
          />
        </ReportAnalyticsGrid>
      ) : null}

      <section className="content-card report-list-card">
        <div className="table-toolbar">
          <label className="billing-limit-select">
            Show
            <select value={filters.limit} disabled={query.isFetching} onChange={(event) => changeLimit(Number(event.target.value))}>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
            entries
          </label>
        </div>

        {query.isLoading ? <LoadingState /> : null}
        {query.isError ? <ErrorState title="Unable to load birth report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess && rows.length === 0 ? <EmptyState title="No birth records found" message="Change filters or date range and search again." /> : null}
        {query.isSuccess && rows.length > 0 ? (
          <>
            <div className="report-table-scroll">
              <table className="report-data-table birth-report-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <SortableTh control={tableSort} column="name">Name</SortableTh>
                    <SortableTh control={tableSort} column="gender">Gender</SortableTh>
                    <th>Guardian</th>
                    <SortableTh control={tableSort} column="doctor">Doctor</SortableTh>
                    <SortableTh control={tableSort} column="dateOfBirth">Birth Date</SortableTh>
                    <th>Weight</th>
                    <SortableTh control={tableSort} column="deliveryMode">Delivery</SortableTh>
                    <SortableTh control={tableSort} column="babyStatus">Status</SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                      <td>{row.name}</td>
                      <td>{row.gender}</td>
                      <td>{text(row.guardianName)}</td>
                      <td>{doctorText(row.doctorName)}</td>
                      <td>{row.dateOfBirth ? formatIpdDateTime(row.dateOfBirth) : '-'}</td>
                      <td>{text(row.weight)}</td>
                      <td>{text(row.deliveryMode)}</td>
                      <td>{row.babyStatus}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination">
              <button className="button button-secondary" disabled={query.isFetching || filters.page <= 1} onClick={() => setFilters((current) => ({ ...current, page: current.page - 1 }))}>Previous</button>
              <span>Page {filters.page} of {Math.max(query.data.pagination.totalPages, 1)}</span>
              <button className="button button-secondary" disabled={query.isFetching || filters.page >= query.data.pagination.totalPages} onClick={() => setFilters((current) => ({ ...current, page: current.page + 1 }))}>Next</button>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};

const SummaryTile = ({ label, value }: { label: string; value: number }) => (
  <div className="report-summary-tile">
    <span>{label}</span>
    <strong>{value.toLocaleString('en-IN')}</strong>
  </div>
);
